import React, { useId } from 'react';
import type { FormFieldProps } from './types';

// ─── FormField ────────────────────────────────────────────────────────────────

export function FormField({
  label,
  htmlFor,
  error,
  helperText,
  required = false,
  children,
}: FormFieldProps) {
  const errorId = useId();
  const hasError = Boolean(error);

  // Inject aria-describedby onto the direct child input element when there is
  // an error message, so screen readers announce it. Falls back gracefully if
  // the child is not a clonable element.
  const child = (() => {
    if (!hasError || !React.isValidElement(children)) return children;
    const existing = (children.props as Record<string, unknown>)['aria-describedby'];
    const combined = existing ? `${existing} ${errorId}` : errorId;
    return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
      'aria-describedby': combined,
      ...(required ? { 'aria-required': true } : {}),
    });
  })();

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label
          htmlFor={htmlFor}
          className={[
            'text-sm font-medium leading-none',
            hasError
              ? 'text-danger-700 dark:text-danger-400'
              : 'text-neutral-700 dark:text-neutral-300',
          ].join(' ')}
        >
          {label}
          {required && (
            <span className="ml-0.5 text-danger-500" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      {child}

      {hasError ? (
        <span
          id={errorId}
          role="alert"
          className="text-xs text-danger-600 dark:text-danger-400"
        >
          {error}
        </span>
      ) : helperText ? (
        <span className="text-xs text-neutral-500 dark:text-neutral-400">{helperText}</span>
      ) : null}
    </div>
  );
}
